import type { Field, FieldValue, Proposal, ProposalRow, Values } from './types.ts';

/**
 * Keyboard grid state for a proposal, kept free of React so the shortcuts are unit-tested
 * (test/companion-grid.test.ts). The grid components render it; the panel sends the result on confirm.
 */

export interface GridRow {
  id: string;
  included: boolean;
  proposed: Values;
  /** The person changed a cell since the companion proposed it. */
  edited: boolean;
  /** Rows with errors cannot be included (the server would refuse them too). */
  blocked: boolean;
}

export interface GridState {
  rows: GridRow[];
  cursor: number;
  /** Index into the editable fields. */
  column: number;
  /** Start of a shift+j/k selection, or null (then the selection is the cursor row). */
  anchor: number | null;
  editing: { rowId: string; field: Field; draft: string } | null;
}

export type GridAction =
  | { type: 'reset'; proposal: Proposal }
  | { type: 'move'; delta: number; extend?: boolean }
  | { type: 'column'; delta: number; fields: number }
  | { type: 'toggle' }
  | { type: 'acceptAll' }
  | { type: 'startEdit'; field: Field; value: string }
  | { type: 'draft'; value: string }
  | { type: 'commitEdit' }
  | { type: 'cancelEdit' }
  | { type: 'setValue'; rowId: string; field: Field; value: FieldValue };

const hasError = (row: ProposalRow) => row.warnings.some((w) => w.level === 'error');

export function initialGrid(proposal: Proposal): GridState {
  return {
    rows: proposal.rows.map((row) => ({ id: row.id, included: row.included, proposed: { ...row.proposed }, edited: false, blocked: hasError(row) })),
    cursor: 0,
    column: 0,
    anchor: null,
    editing: null,
  };
}

const clamp = (value: number, length: number) => (length === 0 ? 0 : Math.min(length - 1, Math.max(0, value)));

/** Parses what a person typed into a cell into the value the server expects. */
export function parseCell(field: Field, text: string): FieldValue {
  if (field === 'hidden') return /^(y|yes|true|1|hidden|hide)$/i.test(text.trim());
  if (field === 'endsAt') {
    const value = text.trim();
    if (value === '' || /^(none|never|no end)$/i.test(value)) return null;
    if (/^now$/i.test(value)) return 'now';
    // A plain date means the end of that day (UTC).
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T23:59:00.000Z`;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? value : new Date(parsed).toISOString();
  }
  return text;
}

/** The text an editor starts from. */
export function cellText(field: Field, value: FieldValue | undefined): string {
  if (value === undefined || value === null) return field === 'endsAt' ? '' : '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return value;
}

export function gridReducer(state: GridState, action: GridAction): GridState {
  switch (action.type) {
    case 'reset':
      return initialGrid(action.proposal);
    case 'move': {
      const cursor = clamp(state.cursor + action.delta, state.rows.length);
      return { ...state, cursor, anchor: action.extend ? (state.anchor ?? state.cursor) : null, editing: null };
    }
    case 'column':
      return { ...state, column: clamp(state.column + action.delta, action.fields) };
    case 'toggle': {
      const ids = new Set(selectedIds(state));
      const anyIncluded = state.rows.some((row) => ids.has(row.id) && row.included);
      return { ...state, rows: state.rows.map((row) => (ids.has(row.id) ? { ...row, included: row.blocked ? false : !anyIncluded } : row)) };
    }
    case 'acceptAll':
      return { ...state, rows: state.rows.map((row) => ({ ...row, included: !row.blocked })) };
    case 'startEdit': {
      const row = state.rows[state.cursor];
      if (!row) return state;
      return { ...state, editing: { rowId: row.id, field: action.field, draft: action.value } };
    }
    case 'draft':
      return state.editing ? { ...state, editing: { ...state.editing, draft: action.value } } : state;
    case 'cancelEdit':
      return { ...state, editing: null };
    case 'commitEdit': {
      if (!state.editing) return state;
      const { rowId, field, draft } = state.editing;
      return { ...gridReducer(state, { type: 'setValue', rowId, field, value: parseCell(field, draft) }), editing: null };
    }
    case 'setValue':
      return {
        ...state,
        rows: state.rows.map((row) =>
          row.id === action.rowId ? { ...row, proposed: { ...row.proposed, [action.field]: action.value }, edited: true, included: row.blocked ? row.included : true } : row,
        ),
      };
  }
}

/** Rows a command applies to: the shift+j/k range, or the cursor row. */
export function selectedIds(state: GridState): string[] {
  if (state.rows.length === 0) return [];
  const from = Math.min(state.anchor ?? state.cursor, state.cursor);
  const to = Math.max(state.anchor ?? state.cursor, state.cursor);
  return state.rows.slice(from, to + 1).map((row) => row.id);
}

export type GridCommand = 'down' | 'up' | 'extendDown' | 'extendUp' | 'left' | 'right' | 'edit' | 'toggle' | 'acceptAll' | 'redo' | 'confirm' | 'discard' | 'undo';

/** Grid shortcuts (outside a cell editor). */
export function gridCommand(event: { key: string; shiftKey: boolean; metaKey: boolean; ctrlKey: boolean; altKey: boolean }): GridCommand | null {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') return 'confirm';
  if (event.metaKey || event.ctrlKey || event.altKey) return null;
  switch (event.key) {
    case 'j':
    case 'ArrowDown':
      return 'down';
    case 'k':
    case 'ArrowUp':
      return 'up';
    case 'J':
      return 'extendDown';
    case 'K':
      return 'extendUp';
    case 'h':
    case 'ArrowLeft':
      return 'left';
    case 'l':
    case 'ArrowRight':
      return 'right';
    case 'Enter':
      return 'edit';
    case 'x':
    case ' ':
      return 'toggle';
    case 'a':
      return 'acceptAll';
    case 'r':
      return 'redo';
    case 'Escape':
      return 'discard';
    case 'u':
      return 'undo';
    default:
      return null;
  }
}

/** What confirm sends: every row's include flag, plus the values of edited rows. */
export function confirmPayload(state: GridState): { rows: { id: string; included: boolean; proposed?: Values }[] } {
  return { rows: state.rows.map((row) => (row.edited ? { id: row.id, included: row.included, proposed: row.proposed } : { id: row.id, included: row.included })) };
}

export interface DiffPart {
  text: string;
  changed: boolean;
}

/** Word-level difference: the common start and end stay plain, the middle is marked (for current → proposed cells). */
export function diffWords(from: string, to: string): { from: DiffPart[]; to: DiffPart[] } {
  const a = from.split(/(\s+)/);
  const b = to.split(/(\s+)/);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let end = 0;
  while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) end++;
  const parts = (words: string[]): DiffPart[] =>
    [
      { text: words.slice(0, start).join(''), changed: false },
      { text: words.slice(start, words.length - end).join(''), changed: true },
      { text: words.slice(words.length - end).join(''), changed: false },
    ].filter((part) => part.text.length > 0);
  return { from: parts(a), to: parts(b) };
}

/** Counts for the footer: included rows that change something, warnings among them. */
export function gridSummary(proposal: Proposal, state: GridState): { included: number; changed: number; warnings: number } {
  const byId = new Map(proposal.rows.map((row) => [row.id, row]));
  let included = 0;
  let changed = 0;
  let warnings = 0;
  for (const row of state.rows) {
    if (!row.included) continue;
    included++;
    const original = byId.get(row.id)!;
    if (proposal.fields.some((field) => (row.proposed[field] ?? null) !== (original.current[field] ?? null))) changed++;
    if (original.warnings.some((w) => w.level === 'warning')) warnings++;
  }
  return { included, changed, warnings };
}
